import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import TextArea from 'devextreme-react/text-area';
import { Tooltip } from 'devextreme-react/tooltip';

import AntLocalization from '../utils/AntLocalization';
import resolveBatchConfirmationColumns from '../utils/AntBatchActionColumns';
import getGridDisplayValue from '../utils/AntGridDisplayValue';
import { batchActionErrorText, batchActionFailedRows, batchActionFailureText } from '../utils/AntBatchActionOutcome';
import AntPopup from './AntPopup';

const T = (value) => AntLocalization.translateText(value);

const getActionCode = (action) =>
    action?.batchActionName || action?.actionName || action?.code || (typeof action?.name === 'string' ? action.name : undefined);

const getActionText = (action) =>
    T(action?.text || action?.label || (typeof action?.name === 'object' || action?.name instanceof Array ? action.name : null) || getActionCode(action));

const unwrapData = (result) => (result && result.data !== undefined ? result.data : result);
const REVIEW_PAGE_SIZE = 12;

const getEligibilityText = (reason) => {
    if (!reason) return T({ vi: 'Không đủ điều kiện', en: 'Not eligible', fr: 'Non éligible', es: 'No elegible', id: 'Tidak memenuhi syarat', ja: '条件未達', zh: '不符合条件', ko: '조건 미충족', hi: 'अपात्र', ar: 'غير مؤهل' });
    if (typeof reason === 'object') return batchActionErrorText(reason, T) || T({ vi: 'Không đủ điều kiện', en: 'Not eligible', fr: 'Non éligible', es: 'No elegible', id: 'Tidak memenuhi syarat', ja: '条件未達', zh: '不符合条件', ko: '조건 미충족', hi: 'अपात्र', ar: 'غير مؤهل' });
    if (reason === 'Object no longer exists') return T({ vi: 'Dòng không còn tồn tại', en: 'The row no longer exists', fr: 'La ligne n\'existe plus', es: 'La fila ya no existe', id: 'Baris sudah tidak ada', ja: '行は存在しません', zh: '该行已不存在', ko: '행이 더 이상 없습니다', hi: 'पंक्ति अब मौजूद नहीं है', ar: 'الصف لم يعد موجودًا' });
    if (reason === 'Action is no longer available for this row')
        return T({ vi: 'Thao tác không còn áp dụng cho dòng này', en: 'This action is no longer available for this row', fr: 'Cette action n\'est plus disponible pour cette ligne', es: 'Esta acción ya no está disponible para esta fila', id: 'Tindakan ini tidak lagi berlaku untuk baris ini', ja: 'この操作はこの行では実行できなくなりました', zh: '此操作不再适用于该行', ko: '이 작업은 이 행에 더 이상 적용되지 않습니다', hi: 'यह क्रिया अब इस पंक्ति पर लागू नहीं है', ar: 'هذا الإجراء لم يعد متاحًا لهذا الصف' });
    if (reason === 'Action validation failed for this row')
        return T({ vi: 'Dòng không đạt điều kiện thực hiện', en: 'The row does not meet the execution conditions', fr: 'La ligne ne remplit pas les conditions d\'exécution', es: 'La fila no cumple las condiciones de ejecución', id: 'Baris tidak memenuhi syarat pelaksanaan', ja: '行が実行条件を満たしていません', zh: '该行不满足执行条件', ko: '행이 실행 조건을 충족하지 않습니다', hi: 'पंक्ति निष्पादन की शर्तें पूरी नहीं करती', ar: 'الصف لا يستوفي شروط التنفيذ' });
    return reason;
};

export default forwardRef(function AntBatchActionPopup(props, ref) {
    const popup = useRef();
    const [state, setState] = useState({ action: null, rows: [], eligibleRows: [], preparing: false, error: null, note: '' });
    const prepareSequence = useRef(0);
    const prepareTimer = useRef();

    const keyExpr = props.keyExpr || 'id';
    const actionText = getActionText(state.action);
    const canExecute =
        !state.preparing &&
        state.rows.length > 0 &&
        state.eligibleRows.length === state.rows.length &&
        (!state.action?.batchNote?.required || Boolean(state.note.trim()));

    const columns = useMemo(
        () =>
            resolveBatchConfirmationColumns(state.action, props.columns, { confirmationColumns: props.confirmationColumns }).map((column) => ({
                ...column,
                dataField: column.dataField,
                caption: T(column.caption || column.label || column.dataField),
                width: column.width,
                // Confirmation grids do not mount AntGrid's named templates,
                // but retain its calculateDisplayValue/format pipeline and use
                // a safe text fallback for object-valued lookup fields.
                cellRender: (cell) => getGridDisplayValue(cell, column)
            })),
        [state.action, props.columns, props.confirmationColumns]
    );

    const widgets = useMemo(
        () =>
            [
                {
                    id: 'btnExecute',
                    icon: 'video',
                    text: T({ vi: 'Thực hiện', en: 'Execute', fr: 'Exécuter', es: 'Ejecutar', id: 'Jalankan', ja: '実行', zh: '执行', ko: '실행', hi: 'चलाएँ', ar: 'تنفيذ' }),
                    waiting_text: T({ vi: 'Đang xử lý...', en: 'Processing...', fr: 'Traitement...', es: 'Procesando...', id: 'Memproses...', ja: '処理中...', zh: '处理中...', ko: '처리 중...', hi: 'प्रसंस्करण...', ar: 'جاري المعالجة...' }),
                    width: 180,
                    disabled: !canExecute
                }
            ],
        [actionText, canExecute, state.action]
    );

    const prepare = useCallback(
        async (action, rows, note) => {
            const sequence = ++prepareSequence.current;
            if (action.batchNote?.required && !note.trim()) {
                setState((current) => ({ ...current, rows, eligibleRows: [], preparing: false, error: null }));
                return;
            }
            setState((current) => ({ ...current, preparing: true, error: null }));
            const actionName = getActionCode(action);
            const ids = rows.map((row) => row.id);
            const actionAPI = action.API || props.API;
            const options = action.batchNote ? { ...action.options, note: note.trim() } : action.options;
            let result;
            try {
                result = actionAPI?.postBatchExecutePrepare
                    ? await actionAPI.postBatchExecutePrepare({ actionName, ids, options })
                    : null;
            } catch (error) {
                result = { error };
            }
            if (sequence !== prepareSequence.current) return;
            const data = unwrapData(result);

            if (!data || result?.error || data.error) {
                setState((current) => ({
                    ...current,
                    preparing: false,
                    error: T({ vi: 'Không kiểm tra được điều kiện thực hiện.', en: 'Could not validate the selected rows.', fr: 'Impossible de vérifier les lignes sélectionnées.', es: 'No se pudieron validar las filas seleccionadas.', id: 'Tidak dapat memeriksa baris yang dipilih.', ja: '選択した行を検証できませんでした。', zh: '无法校验所选行。', ko: '선택한 행을 검증할 수 없습니다.', hi: 'चयनित पंक्तियों की जाँच नहीं हो सकी।', ar: 'تعذر التحقق من الصفوف المحددة.' })
                }));
                return;
            }

            const preparedRows = data.rows instanceof Array ? data.rows : [];
            const preparedById = new Map(preparedRows.map((row) => [String(row.id), row]));
            const mergedRows = rows.map((row) => {
                const prepared = preparedById.get(String(row.id));
                return {
                    ...row,
                    batchAction: prepared?.batchAction || {
                        canExecute: false,
                        reason: T({ vi: 'Dòng không còn tồn tại hoặc không đủ điều kiện.', en: 'The row no longer exists or is not eligible.', fr: 'La ligne n\'existe plus ou n\'est pas éligible.', es: 'La fila ya no existe o no es elegible.', id: 'Baris sudah tidak ada atau tidak memenuhi syarat.', ja: '行が存在しないか、条件を満たしていません。', zh: '该行已不存在或不符合条件。', ko: '행이 없거나 조건을 충족하지 않습니다.', hi: 'पंक्ति मौजूद नहीं है या अपात्र है।', ar: 'الصف لم يعد موجودًا أو غير مؤهل.' })
                    }
                };
            });
            setState((current) => ({ ...current, rows: mergedRows, eligibleRows: mergedRows.filter((row) => row.batchAction?.canExecute), preparing: false, error: null }));
        },
        [props.API]
    );

    const show = useCallback(
        (action, selectedRows) => {
            const rows = selectedRows instanceof Array ? selectedRows.filter((row) => row && row.id !== undefined) : [];
            if (!action || rows.length <= 0) return;
            clearTimeout(prepareTimer.current);
            ++prepareSequence.current;
            setState({ action, rows, eligibleRows: [], preparing: true, error: null, note: '' });
            if (popup.current) popup.current.show();
            prepare(action, rows, '');
        },
        [prepare]
    );

    const onNoteChanged = useCallback((value) => {
        const note = String(value || '');
        clearTimeout(prepareTimer.current);
        ++prepareSequence.current;
        setState((current) => ({ ...current, note, eligibleRows: [], preparing: true, error: null }));
        prepareTimer.current = setTimeout(() => prepare(state.action, state.rows, note), 350);
    }, [prepare, state.action, state.rows]);

    useEffect(() => () => {
        clearTimeout(prepareTimer.current);
        ++prepareSequence.current;
    }, []);

    useImperativeHandle(ref, () => ({ show }), [show]);

    useEffect(() => {
        if (!state.preparing && state.action && state.rows.length <= 0 && popup.current) popup.current.close();
    }, [state.action, state.preparing, state.rows.length]);

    const removeRow = useCallback(
        (row) => {
            const rowId = row?.[keyExpr];
            setState((current) => {
                const rows = current.rows.filter((item) => String(item?.[keyExpr]) !== String(rowId));
                return {
                    ...current,
                    rows,
                    eligibleRows: current.eligibleRows.filter((item) => String(item?.[keyExpr]) !== String(rowId))
                };
            });
        },
        [keyExpr]
    );

    const renderStatusIcon = useCallback(
        (info) => {
            if (state.preparing)
                return <i className="dx-icon dx-icon-refresh ant-batch-action-status-pending" title={T({ vi: 'Đang kiểm tra', en: 'Validating', fr: 'Vérification', es: 'Validando', id: 'Memeriksa', ja: '検証中', zh: '校验中', ko: '검증 중', hi: 'जाँच जारी', ar: 'جارٍ التحقق' })} />;

            const canExecuteRow = Boolean(info.data?.batchAction?.canExecute);
            if (canExecuteRow)
                return (
                    <Button
                        className="ant-grid-icon ant-icon-valid"
                        type="normal"
                        stylingMode="text"
                        focusStateEnabled={false}
                        hoverStateEnabled={false}
                        activeStateEnabled={false}
                    >
                        <i className="dx-icon dx-icon-check" />
                    </Button>
                );

            const targetId = `ant-batch-action-warning-${String(info.data?.[keyExpr]).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
            return (
                <React.Fragment>
                    <Button
                        id={targetId}
                        className="ant-grid-icon ant-icon-error"
                        type="normal"
                        stylingMode="text"
                        focusStateEnabled={false}
                        hoverStateEnabled={false}
                        activeStateEnabled={false}
                    >
                        <i className="dx-icon dx-icon-info" />
                    </Button>
                    <Tooltip
                        target={`#${targetId}`}
                        className="ant-tooltip"
                        hideEvent="mouseleave"
                        showEvent={{ name: 'mouseenter dxclick keyup' }}
                        hideOnOutsideClick={true}
                    >
                        <div>{getEligibilityText(info.data?.batchAction?.reason)}</div>
                    </Tooltip>
                </React.Fragment>
            );
        },
        [keyExpr, state.preparing]
    );

    const renderRemoveButton = useCallback(
        (info) => (
            <Button
                className="ant-grid-icon"
                hint={T({ vi: 'Bỏ dòng này', en: 'Remove this row', fr: 'Retirer la ligne', es: 'Quitar esta fila', id: 'Hapus baris ini', ja: 'この行を除外', zh: '移除此行', ko: '이 행 제외', hi: 'यह पंक्ति हटाएँ', ar: 'إزالة هذا الصف' })}
                onClick={() => removeRow(info.data)}
                type="normal"
                stylingMode="text"
                focusStateEnabled={false}
                hoverStateEnabled={false}
            >
                <i className="dx-icon icon dx-icon-trash" />
            </Button>
        ),
        [removeRow]
    );

    const onAction = useCallback(
        async (button) => {
            if (button?.id !== 'btnExecute') return true;
            // Keep this guard even though AntPopup disables the button: callers
            // can invoke onAction programmatically and must not bypass prepare.
            if (!canExecute) return false;

            const actionName = getActionCode(state.action);
            const ids = state.eligibleRows.map((row) => row.id);
            const actionAPI = state.action?.API || props.API;
            let result;
            try {
                result = await actionAPI.postBatchExecute({
                    actionName,
                    ids,
                    options: state.action?.batchNote ? { ...state.action.options, note: state.note.trim() } : state.action?.options
                });
            } catch (error) {
                result = { error };
            }
            const data = unwrapData(result);
            const succeeded = Number(data?.executed_qty || data?.succeeded_qty || 0);
            const failed = Number(data?.failed_qty || 0);

            if (!data || result?.error || data.error || (!data.queued && succeeded <= 0)) {
                setState((current) => ({
                    ...current,
                    error: batchActionFailureText(result?.error ? result : data || result, ids.length, T)
                }));
                return false;
            }

            if (props.onExecuted) await props.onExecuted({ action: state.action, result: data });
            if (failed > 0) {
                const failedRows = batchActionFailedRows(state.rows, data);
                setState((current) => ({
                    ...current,
                    rows: failedRows.length ? failedRows : current.rows,
                    eligibleRows: [],
                    error: batchActionFailureText(data, ids.length, T)
                }));
                return false;
            }
            return true;
        },
        [canExecute, props.API, props.onExecuted, state.action, state.eligibleRows, state.note]
    );

    const invalidCount = state.rows.length - state.eligibleRows.length;
    const confirmText = state.action?.batchNote?.required && !state.note.trim()
        ? T({ vi: 'Nhập lý do để kiểm tra các dòng đã chọn.', en: 'Enter a reason to validate the selected rows.', fr: 'Saisissez un motif pour vérifier les lignes choisies.', es: 'Ingrese un motivo para validar las filas elegidas.', id: 'Masukkan alasan untuk memeriksa baris terpilih.', ja: '選択した行を検証するには理由を入力してください。', zh: '请输入原因以校验所选行。', ko: '선택한 행을 검증하려면 사유를 입력하세요.', hi: 'चयनित पंक्तियों की जाँच हेतु कारण दर्ज करें।', ar: 'أدخل سببًا للتحقق من الصفوف المحددة.' })
        : state.preparing
        ? T({ vi: 'Đang kiểm tra điều kiện thực hiện...', en: 'Validating selected rows...', fr: 'Vérification des lignes sélectionnées...', es: 'Validando las filas seleccionadas...', id: 'Memeriksa baris terpilih...', ja: '選択した行を検証中...', zh: '正在校验所选行...', ko: '선택한 행 검증 중...', hi: 'चयनित पंक्तियों की जाँच जारी...', ar: 'جارٍ التحقق من الصفوف المحددة...' })
        : canExecute
        ? `${T({ vi: 'Xác nhận thực hiện', en: 'Confirm', fr: 'Confirmer', es: 'Confirmar', id: 'Konfirmasi', ja: '確認:', zh: '确认执行', ko: '실행 확인:', hi: 'पुष्टि करें:', ar: 'تأكيد تنفيذ' })} “${actionText}” ${T({ vi: 'cho', en: 'for', fr: 'pour', es: 'para', id: 'untuk', ja: '対象', zh: '共', ko: '대상', hi: 'कुल', ar: 'على' })} ${state.eligibleRows.length} ${T({
              vi: 'dòng đã chọn?',
              en: 'selected rows?',
              fr: 'lignes choisies ?',
              es: 'filas elegidas?',
              id: 'baris terpilih?',
              ja: '行を実行？',
              zh: '个所选行？',
              ko: '개 선택 행?',
              hi: 'चयनित पंक्तियाँ?',
              ar: 'صفوف محددة؟'
          })}`
        : T({ vi: 'Các dòng đã chọn chưa đồng thời thỏa điều kiện thực hiện.', en: 'The selected rows are not all eligible for this action.', fr: 'Les lignes sélectionnées ne sont pas toutes éligibles à cette action.', es: 'No todas las filas seleccionadas son elegibles para esta acción.', id: 'Tidak semua baris terpilih memenuhi syarat untuk tindakan ini.', ja: '選択した行の一部がこの操作の条件を満たしていません。', zh: '所选行并非全部符合此操作的条件。', ko: '선택한 행 중 일부가 이 작업 조건을 충족하지 않습니다.', hi: 'सभी चयनित पंक्तियाँ इस क्रिया के लिए पात्र नहीं हैं।', ar: 'ليست كل الصفوف المحددة مؤهلة لهذا الإجراء.' });

    return (
        <AntPopup
            ref={popup}
            isGray={true}
            width={props.width || 900}
            height={Math.min(760, Math.max(360, Math.min(state.rows.length, REVIEW_PAGE_SIZE) * 34 + 310 + (state.action?.batchNote ? 90 : 0)))}
            autoHeight={true}
            autoWidth={true}
            deferRendering={true}
            title={`${T({ vi: 'Xử lý hàng loạt', en: 'Batch action', fr: 'Action groupée', es: 'Acción masiva', id: 'Aksi massal', ja: '一括処理', zh: '批量处理', ko: '일괄 처리', hi: 'बैच क्रिया', ar: 'إجراء جماعي' })}${actionText ? ` — ${actionText}` : ''}`}
            widgets={widgets}
            onAction={onAction}
            errorText={state.error}
            errors={state.error ? [state.error] : undefined}
        >
            <div className="ant-batch-action-confirm-text">{confirmText}</div>
            {state.action?.batchNote && (
                <div className="ant-batch-action-note">
                    <label>{T(state.action.batchNote.label || { vi: 'Ghi chú', en: 'Note', fr: 'Note', es: 'Nota', id: 'Catatan', ja: 'メモ', zh: '备注', ko: '메모', hi: 'नोट', ar: 'ملاحظة' })}</label>
                    <TextArea value={state.note} onValueChanged={(event) => onNoteChanged(event.value)} height={70} />
                </div>
            )}
            {!state.preparing && invalidCount > 0 && (
                <div className="ant-batch-action-warning">
                    {`${invalidCount}/${state.rows.length} ${T({ vi: 'dòng không đủ điều kiện.', en: 'rows are not eligible.', fr: 'lignes non éligibles.', es: 'filas no elegibles.', id: 'baris tidak memenuhi syarat.', ja: '行が条件を満たしていません。', zh: '行不符合条件。', ko: '개 행이 조건 미충족.', hi: 'पंक्तियाँ अपात्र हैं।', ar: 'صفوف غير مؤهلة.' })}`}
                </div>
            )}
            <DataGrid
                className="dx-card wide-card ant-batch-action-grid"
                dataSource={state.rows}
                keyExpr={keyExpr}
                focusedRowEnabled={false}
                columnAutoWidth={true}
                showBorders={true}
                showRowLines={true}
                height={Math.min(450, Math.max(160, Math.min(state.rows.length, REVIEW_PAGE_SIZE) * 34 + 86))}
            >
                <Paging pageSize={props.pageSize || REVIEW_PAGE_SIZE} />
                <Pager
                    visible={state.rows.length > (props.pageSize || REVIEW_PAGE_SIZE)}
                    showInfo={true}
                    infoText={`{2} ${T({ vi: 'dòng', en: 'records', fr: 'lignes', es: 'filas', id: 'baris', ja: '行', zh: '行', ko: '행', hi: 'पंक्तियाँ', ar: 'صفوف' })}`}
                />
                <Column name="remove" caption=" " width={40} alignment="center" cellRender={renderRemoveButton} />
                <Column name="cellStatus" caption=" " width={40} alignment="center" cellRender={renderStatusIcon} />
                {columns.map((column) => (
                    <Column key={column.dataField} {...column} />
                ))}
            </DataGrid>
        </AntPopup>
    );
});
